import {
  taskStatusesCreateMutation,
  taskStatusesListOptions,
  taskStatusesListQueryKey,
  taskStatusesPartialUpdateMutation,
  taskStatusesRetrieveOptions,
  TaskStatusReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryUpdate,
  useTableSorter
} from '@shared/hooks'
import { getContrastTextColor, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, TableColumnsType, Tag, theme, Tooltip } from 'antd'
import { MenuProps } from 'antd/lib'
import {
  Add,
  CloseSquare,
  Edit,
  Refresh,
  TickSquare,
  Trash
} from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormModal, FormNomenclaturesTaskStatuses } from '../components'

const formName = 'taskStatuses'

const DEFAULT_TASK_CODE_STATUSES = {
  toDo: 'to-do',
  done: 'done'
} as const
type DefaultTaskCodeStatus =
  (typeof DEFAULT_TASK_CODE_STATUSES)[keyof typeof DEFAULT_TASK_CODE_STATUSES]

export const NomenclaturesTasksStatuses = () => {
  const [isCreate, setIsCreate] = useState(false)
  const [editItemId, setEditItemId] = useState<number>()
  const [deactivateItem, setDeactivateItem] = useState<TaskStatusReadable>()

  const { notification } = App.useApp()
  const { params } = useQueryUpdate()
  const sorter = useTableSorter()
  const { token } = theme.useToken()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { t } = useTranslation()

  const { data, isLoading } = useQuery({
    ...taskStatusesListOptions({
      query: { ...defaultFilters, ...params }
    })
  })

  const { mutateAsync, isPending } = useMutation({
    ...taskStatusesPartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries([taskStatusesListQueryKey()])
      onCloseModal()
      notification.success({
        message: t('success.success')
      })
    },
    onError: (e) => showApiErrors(e)
  })

  const onCloseModal = () => {
    setDeactivateItem(undefined)
    setIsCreate(false)
    setEditItemId(undefined)
  }
  const onCreate = () => setIsCreate(true)
  const onEdit = (id: number) => setEditItemId(id)
  const onDeactivate = async () => {
    if (deactivateItem) {
      await mutateAsync({
        path: { id: deactivateItem.id },
        body: { is_active: !deactivateItem.is_active }
      })
    }
  }

  const isDefaultTaskCodeStatus = (
    status: string
  ): status is DefaultTaskCodeStatus => {
    return Object.values(DEFAULT_TASK_CODE_STATUSES).includes(
      status as DefaultTaskCodeStatus
    )
  }

  const getActionColumnItem: (
    item: TaskStatusReadable
  ) => MenuProps['items'] = (item) => [
    {
      label: (
        <Tooltip
          title={
            isDefaultTaskCodeStatus(item.code) &&
            t('task.canNotModifySystemStatus')
          }
        >
          {t('nomenclature.edit')}
        </Tooltip>
      ),
      icon: <Edit size={20} />,
      key: 'edit',
      disabled: isDefaultTaskCodeStatus(item.code),
      onClick: () => onEdit(item.id)
    },
    { type: 'divider' },
    {
      label: (
        <Tooltip
          title={
            isDefaultTaskCodeStatus(item.code) &&
            t('task.canNotModifySystemStatus')
          }
        >
          {t(
            item.is_active ? 'nomenclature.deactivate' : 'nomenclature.activate'
          )}
        </Tooltip>
      ),
      icon: item.is_active ? <Trash size={20} /> : <Refresh size={20} />,
      disabled: isDefaultTaskCodeStatus(item.code),
      danger: item.is_active,
      key: 'delete',
      onClick: () => setDeactivateItem(item)
    }
  ]

  const columns: TableColumnsType<TaskStatusReadable> = [
    {
      title: t('nomenclature.name'),
      key: 'name',
      dataIndex: 'name',
      sorter: true
    },
    {
      title: t('nomenclature.code'),
      key: 'code',
      dataIndex: 'code'
    },
    {
      title: t('nomenclature.order'),
      key: 'order',
      dataIndex: 'order',
      sorter: true
    },
    {
      title: t('nomenclature.color'),
      key: 'color',
      dataIndex: 'color',
      render: (color?: string) => (
        <Tag
          color={color ?? 'default'}
          style={{ color: getContrastTextColor(color) }}
        >
          {color}
        </Tag>
      )
    },
    {
      title: t('nomenclature.status'),
      key: 'is_active',
      dataIndex: 'is_active',
      sorter: true,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {t(isActive ? 'campaigns.stats_active' : 'nomenclature.inactive')}
        </Tag>
      )
    },
    {
      title: t('nomenclature.default'),
      dataIndex: 'is_default',
      align: 'center',
      render: (_, { code }) =>
        code === DEFAULT_TASK_CODE_STATUSES.toDo ? (
          <TickSquare color={token.colorSuccess} />
        ) : (
          <CloseSquare color={token.colorError} />
        )
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (created_at) => <DayLabel date={created_at} />
    },
    {
      title: t('lead.modified_at'),
      dataIndex: 'modified_at',
      key: 'modified_at',
      sorter: true,
      render: (modified_at) => <DayLabel date={modified_at} />
    },
    {
      width: 50,
      fixed: 'right',
      render: (_, record) => (
        <TableActions options={getActionColumnItem(record)} />
      )
    }
  ]

  return (
    <>
      <TableContainer
        rowKey="id"
        data={data}
        columns={columns}
        loading={isLoading}
        bordered
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <Button type="primary" icon={<Add size={20} />} onClick={onCreate}>
            {t('nomenclature.add_status')}
          </Button>
        }
      />

      <ConfirmModal
        open={!!deactivateItem}
        onSuccess={onDeactivate}
        onCancel={onCloseModal}
        okButtonProps={{ loading: isPending }}
      >
        {t(
          deactivateItem?.is_active
            ? 'nomenclature.deactivateConfirm'
            : 'nomenclature.activateConfirm'
        )}
      </ConfirmModal>

      <FormModal
        id={editItemId}
        formName={formName}
        open={!!(editItemId || isCreate)}
        getItem={taskStatusesRetrieveOptions}
        onCreate={taskStatusesCreateMutation}
        onUpdate={taskStatusesPartialUpdateMutation}
        onCloseModal={onCloseModal}
        width={700}
      >
        <FormNomenclaturesTaskStatuses />
      </FormModal>
    </>
  )
}
